/* eslint-disable max-lines */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@mui/material';
import { includes, isEmpty } from 'lodash';

const PrescriptionReviewSelectionContext = createContext(null);

export const PrescriptionReviewSelectionProvider = ({ children, initialValue = [] }) => {
  const [selectedItemIds, setSelectedItemIds] = useState(initialValue);

  const updateSelectedItemIds = useCallback(({ isSelected, lineItemId }) => {
    setSelectedItemIds((prev) => {
      if (isSelected) {
        return [...prev, lineItemId];
      }
      return prev.filter((id) => id !== lineItemId);
    });
  }, []);

  const clearSelectedItems = useCallback(() => {
    setSelectedItemIds([]);
  }, []);

  const memoizedValue = useMemo(
    () => ({
      selectedItemIds,
      updateSelectedItemIds,
      clearSelectedItems,
      setSelectedItemIds,
    }),
    [selectedItemIds, clearSelectedItems, updateSelectedItemIds, setSelectedItemIds]
  );

  return (
    <PrescriptionReviewSelectionContext.Provider value={memoizedValue}>
      {children}
    </PrescriptionReviewSelectionContext.Provider>
  );
};

PrescriptionReviewSelectionProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialValue: PropTypes.arrayOf(PropTypes.string),
};

PrescriptionReviewSelectionProvider.defaultProps = {
  initialValue: [],
};

export const usePrescriptionReviewSelection = () => {
  const context = useContext(PrescriptionReviewSelectionContext);
  if (!context) {
    throw new Error(
      'usePrescriptionReviewSelection must be used within PrescriptionReviewSelectionProvider'
    );
  }
  return context;
};

export const PrescriptionSelectionCheckBox = ({ lineItemId, isDisabled }) => {
  const { selectedItemIds, updateSelectedItemIds } = usePrescriptionReviewSelection();

  const isChecked = isDisabled ? false : includes(selectedItemIds, lineItemId);

  const onChangeHandler = (event) => {
    updateSelectedItemIds({
      isSelected: event.target.checked,
      lineItemId,
    });
  };

  return (
    <Checkbox
      id={lineItemId}
      inputProps={{ 'aria-label': `select ${lineItemId}` }}
      size='small'
      sx={{ py: 0 }}
      checked={isChecked}
      onChange={onChangeHandler}
      disabled={isDisabled}
    />
  );
};

PrescriptionSelectionCheckBox.propTypes = {
  lineItemId: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
};

PrescriptionSelectionCheckBox.defaultProps = {
  isDisabled: false,
};

export const SelectAllPrescriptionsCheckBox = ({ allLineItemIds, isDisabled }) => {
  const { selectedItemIds, setSelectedItemIds } = usePrescriptionReviewSelection();

  const isAllSelected =
    !isEmpty(allLineItemIds) && allLineItemIds.length === selectedItemIds.length;
  const isIndeterminate =
    selectedItemIds.length > 0 && selectedItemIds.length < allLineItemIds.length;

  const onChangeHandler = (event) => {
    if (event.target.checked) {
      setSelectedItemIds(allLineItemIds);
    } else {
      setSelectedItemIds([]);
    }
  };

  return (
    <Checkbox
      inputProps={{ 'aria-label': 'select all prescriptions' }}
      size='small'
      sx={{ py: 0 }}
      checked={isAllSelected}
      indeterminate={isIndeterminate}
      onChange={onChangeHandler}
      disabled={isDisabled}
    />
  );
};

SelectAllPrescriptionsCheckBox.propTypes = {
  allLineItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  isDisabled: PropTypes.bool,
};

SelectAllPrescriptionsCheckBox.defaultProps = {
  isDisabled: false,
};
